import { useCallback, useEffect, useRef, useState } from 'react'
import GenericLineChart from 'components/common/GenericLineChart'
import { Line, ResponsiveContainer, Tooltip } from 'recharts'
import { LineChartDatum } from 'components/common/types'
import { useTheme } from '@mui/material/styles'
import { EmployeeOverviewDataKeys } from 'containers/VerticalPage/common/Company/OverviewTab/types'
import EmployeeOvertimeTooltip from 'containers/VerticalPage/common/Company/OverviewTab/EmployeeOvertimeTooltip/EmployeeOvertimeTooltip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { legendData } from './utils'
import CircleIcon from '@mui/icons-material/Circle'

interface EmployeeOverviewChartProps {
  chartData: LineChartDatum[]
}

const EmployeeOverviewChart = ({ chartData }: EmployeeOverviewChartProps) => {
  const [hoveredDot, setHoveredDot] = useState<EmployeeOverviewDataKeys | null>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const theme = useTheme()

  const showTooltip = useCallback((dotType: EmployeeOverviewDataKeys) => {
    clearTimeout(hideTimeoutRef.current)
    setHoveredDot(dotType)
  }, [])

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setHoveredDot(null), 150)
  }, [])

  const keepTooltip = useCallback(() => {
    clearTimeout(hideTimeoutRef.current)
  }, [])

  useEffect(() => {
    return () => clearTimeout(hideTimeoutRef.current)
  }, [])
  return (
    <>
      <ResponsiveContainer width='100%' height='475px'>
        <GenericLineChart
          chartData={chartData}
          xAxisDataKey='year'
          xLabel='Year'
          yLabel='Number Of Employees'
          yAxisDistanceX={-40}
          xAxisDistanceY={15}
          labelstyle={{ fontSize: '12px' }}
          yLabelAngle={-90}
          tick={{ fill: '#1C2842' }}
        >
          <Line
            type='linear'
            dot={{ r: 4 }}
            dataKey={EmployeeOverviewDataKeys.localAmount}
            stroke={theme.palette.companyOverview.localCountry}
            strokeWidth={3}
            activeDot={{
              onMouseEnter: () => showTooltip(EmployeeOverviewDataKeys.localAmount),
              onMouseLeave: hideTooltip,
            }}
            name='Local'
          />
          <Line
            type='linear'
            dot={{ r: 4 }}
            dataKey={EmployeeOverviewDataKeys.restOfTheWorldAmount}
            stroke={theme.palette.companyOverview.restOfTheWorld}
            strokeWidth={3}
            activeDot={{
              onMouseEnter: () => showTooltip(EmployeeOverviewDataKeys.restOfTheWorldAmount),
              onMouseLeave: hideTooltip,
            }}
            name='Non-local'
          />
          <Tooltip
            isAnimationActive={false}
            wrapperStyle={{ pointerEvents: 'auto' }}
            content={
              <EmployeeOvertimeTooltip
                localCountry={'Local'}
                hoveredDot={hoveredDot}
                onMouseEnter={keepTooltip}
                onMouseLeave={hideTooltip}
              />
            }
            cursor={{ stroke: 'none', strokeWidth: 0 }}
          />
        </GenericLineChart>
      </ResponsiveContainer>
      <Stack flexDirection='row' mt={1}>
        {legendData.map((data, index) => (
          <Stack key={index} flexDirection='row' alignItems='center' mr={4}>
            <CircleIcon htmlColor={data!.color} sx={{ width: '10px', height: '10px', marginRight: '6px' }} />
            <Typography color='#1C2842' fontSize={{ md: 15, lg: 15, xl: 16 }} fontFamily='Source Sans Pro'>
              {data!.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  )
}

export default EmployeeOverviewChart
